import { createClient } from '@supabase/supabase-js';
import { getFullStateName } from '../constants/locations';

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export const getApiBaseUrl = () => {
  let raw = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const isLocalUi =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (
    process.env.NODE_ENV === 'development' &&
    isLocalUi &&
    raw &&
    /dev\.krime\.app|krime\.app/i.test(raw)
  ) {
    raw = 'http://localhost:3001';
  }
  if (!raw) return '';
  if (/\/api$/i.test(raw)) return raw;
  return `${raw}/api`;
};

const inFlightRequests = new Map();
const pendingYearChecks = new Map();
const cache = {};

// Per-tab session id: lets the backend group everything a user does in a
// single browsing session for analytics and scraper correlation. Persists
// across SPA navigations in the same tab; new tab = new session.
const SESSION_ID_KEY = 'krime_session_id';
const getSessionId = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.())
        || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// Current frontend URL at request time. Injected as the x-client-url header
// so activityLogger can stash it under metadata.client_url for every row
// without forcing each handler to thread it through.
const getClientUrl = () => {
  try {
    return (typeof window !== 'undefined' && window.location?.href) || null;
  } catch {
    return null;
  }
};

const authenticatedFetch = async (url) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    window.location.href = '/login';
    throw new Error('Not authenticated');
  }

  const sessionId = getSessionId();
  const clientUrl = getClientUrl();
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...(sessionId ? { 'x-session-id': sessionId } : {}),
      ...(clientUrl ? { 'x-client-url': clientUrl } : {})
    }
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status} URL: ${url}`);
  }

  return response.json();
};

const deduplicatedFetch = (url) => {
  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url);
  }

  const promise = authenticatedFetch(url)
    .finally(() => {
      inFlightRequests.delete(url);
    });

  inFlightRequests.set(url, promise);

  return promise;
};

const deduplicatedYearCheck = (key, fetchFn) => {
  if (pendingYearChecks.has(key)) {
    return pendingYearChecks.get(key);
  }

  const promise = fetchFn()
    .finally(() => {
      setTimeout(() => {
        pendingYearChecks.delete(key);
      }, 100);
    });

  pendingYearChecks.set(key, promise);
  return promise;
};

const buildUrl = (base, params) => {
  const url = new URL(`${getApiBaseUrl()}/${base}`);
  Object.keys(params).sort().forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      url.searchParams.append(key, params[key]);
    }
  });
  return url.toString();
};

function getSourcePath(dataSource) {
  switch (dataSource) {
    case 'FBI_CIUS': return 'Federal/FBI_CIUS';
    case 'BJS_NCVS': return 'Federal/BJS_NCVS';
    default: return 'Federal/FBI_CIUS';
  }
}

function parseCountyLocation(locationName) {
  if (locationName && locationName.includes(',')) {
    const parts = locationName.split(',').map(part => part.trim());
    const county = parts.slice(0, -1).join(', ').replace(/\s+(County|Parish|Borough)$/i, '');
    const state = parts[parts.length - 1];
    return { county, state };
  }
  return { county: null, state: null };
}

function parseCityLocation(locationName) {
  if (locationName && locationName.includes(',')) {
    const parts = locationName.split(',').map(part => part.trim());
    const city = parts.slice(0, -1).join(', ');
    const state = parts[parts.length - 1];
    return { city, state };
  }
  return { city: null, state: null };
}

export const checkCityResolution = async (cityName, stateName, dateRange, dataSource = 'FBI_CIUS') => {
  try {
    const endpoint = 'cities';
    const queryParams = { 
      city: cityName, 
      state: stateName, 
      year: dateRange, 
      skipResolutionCheck: 'false',
      geometry: 'false',
      fieldsOnly: 'resolution'
    };
    
    const sourcePath = getSourcePath(dataSource);
    const url = buildUrl(`${sourcePath}/${endpoint}`, queryParams);
    const result = await deduplicatedFetch(url);
    
    return result.requiresCityResolution ? result.cityResolutionData : null;
  } catch (error) {
    console.error('Error checking city resolution:', error);
    return null;
  }
};

export const checkLocationYears = async (locationName, locationType, dataSource = 'FBI_CIUS') => {
  const cacheKey = `years-${locationName}-${locationType}-${dataSource}`;
  
  return deduplicatedYearCheck(cacheKey, async () => {
    try {
      let endpoint;
      let queryParams = {};
      
      switch (locationType) {
        case 'nation':
          if (locationName === 'All Nations') {
            queryParams = { fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, fieldsOnly: 'years' };
          }
          endpoint = 'nation';
          break;
        case 'region':
          endpoint = 'regions';
          if (locationName === 'All Regions') {
            queryParams = { fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, fieldsOnly: 'years' };
          }
          break;
        case 'division':
          endpoint = 'divisions';
          if (locationName === 'All Divisions') {
            queryParams = { fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, fieldsOnly: 'years' };
          }
          break; 
        case 'state':
          endpoint = 'states';
          if (locationName === 'All States') {
            queryParams = { fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, fieldsOnly: 'years' };
          }
          break;
        case 'metro':
          endpoint = 'metros';
          if (locationName === 'All Metros') {
            queryParams = { fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, fieldsOnly: 'years' };
          }
          break;
        case 'county':
          endpoint = 'counties';
          if (locationName.includes(',')) {
            const { county, state } = parseCountyLocation(locationName);
            queryParams = { county, state, geometry: 'false', fieldsOnly: 'years' };
          } else if (locationName.startsWith('All ') && locationName.endsWith(' Counties')) {
            let stateName = locationName.replace(/^All /, '').replace(/ Counties$/, '');
            if (stateName.length === 2) {
              stateName = getFullStateName(stateName);
            }
            queryParams = stateName ? { state: stateName, geometry: 'false', fieldsOnly: 'years' } : { geometry: 'false', fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, geometry: 'false' };
          }
          break;
        case 'city':
          endpoint = 'cities';
          if (locationName.includes(',')) {
            const { city, state } = parseCityLocation(locationName);
            queryParams = { city, state, geometry: 'false', skipResolutionCheck: 'true', fieldsOnly: 'years' };
          } else if (locationName.startsWith('All ') && locationName.endsWith(' Cities')) {
            let stateName = locationName.replace(/^All /, '').replace(/ Cities$/, '');
            if (stateName.length === 2) {
              stateName = getFullStateName(stateName);
            }
            queryParams = stateName ? { state: stateName, geometry: 'false', fieldsOnly: 'years' } : { geometry: 'false', fieldsOnly: 'years' };
          } else {
            queryParams = { name: locationName, geometry: 'false' };
          }
          break;
        default:
          console.error(`Invalid location type: "${locationType}"`);
          return [];
      }
      
      const sourcePath = getSourcePath(dataSource);
      const url = buildUrl(`${sourcePath}/${endpoint}`, queryParams);
      const result = await deduplicatedFetch(url);    

      if (result.constituentYears) {
        return {
          availableYears: result.availableYears || [],
          constituentYears: result.constituentYears
        };
      }
      
      return result.availableYears || [];
      
    } catch (error) {
      console.error('Error checking location years:', error);
      return [];
    }
  });
};

export const fetchLocationData = async (locationName, date, locationType, dataSource = 'FBI_CIUS', includeGeometry = true, selectedCrime = 'all_crime', skipResolutionCheck = false, specificLocationOnly = false) => {
  try {
    if (dataSource === 'BJS_NCVS' && locationType !== 'nation') {
      dataSource = 'FBI_CIUS';
    }
    
    let endpoint;
    let queryParams = {};
    const isNationwideCounties = locationType === 'county' && locationName === 'All Counties';
    const isNationwideCities = locationType === 'city' && locationName === 'All Cities';
    
    switch (locationType) {
      case 'nation':
        endpoint = 'nation';
        queryParams = { year: date };
        break;
      case 'region':
        endpoint = 'regions';
        queryParams = { year: date };
        break;
      case 'division':
        endpoint = 'divisions';
        queryParams = { year: date };
        break;
      case 'state':
        endpoint = 'states';
        queryParams = { year: date };
        break;
      case 'metro':
        endpoint = 'metros';
        queryParams = { year: date };
        break;
      case 'county':
        endpoint = 'counties';
        if (isNationwideCounties) {
          queryParams = { year: date, geometry: 'false', crime: selectedCrime };
        }
        else if (locationName.startsWith('All ') && locationName.endsWith(' Counties')) {
          let stateName = locationName.replace(/^All /, '').replace(/ Counties$/, '');
          
          if (stateName.length === 2) {
            stateName = getFullStateName(stateName);
          }
          
          if (stateName) {
            queryParams = { year: date, geometry: String(includeGeometry), state: stateName, crime: selectedCrime };
          } else {
            queryParams = { year: date, geometry: String(includeGeometry), crime: selectedCrime };
          }
        }
        else if (locationName.includes(',')) {
          const { county, state } = parseCountyLocation(locationName);
          if (county && state) {
            queryParams = { county, state, year: date };
          } else {
            queryParams = { year: date };
          }
        }
        else {
          queryParams = { year: date };
        }
        break;
      case 'city':
        endpoint = 'cities';
        if (isNationwideCities) {
          queryParams = { year: date, geometry: 'false', crime: selectedCrime };
        }
        else if (locationName.startsWith('All ') && locationName.endsWith(' Cities')) {
          let stateName = locationName.replace(/^All /, '').replace(/ Cities$/, '');
          
          if (stateName.length === 2) {
            stateName = getFullStateName(stateName);
          }
          
          if (stateName) {
            queryParams = { year: date, geometry: String(includeGeometry), state: stateName, crime: selectedCrime };
          } else {
            queryParams = { year: date, geometry: String(includeGeometry), crime: selectedCrime };
          }
        }
        else if (locationName.includes(',')) {
          const { city, state } = parseCityLocation(locationName);
          if (city && state) {
            queryParams = { city, state, year: date, skipResolutionCheck: skipResolutionCheck ? 'true' : 'false' };
          } else {
            queryParams = { year: date };
          }
        }
        else {
          queryParams = { year: date };
        }
        break;
      default:
        throw new Error(`Invalid location type: "${locationType}"`);
    }
  
    const sourcePath = getSourcePath(dataSource);
    const locationUrl = buildUrl(`${sourcePath}/${endpoint}`, queryParams);
    const fetchedLocationObject = await deduplicatedFetch(locationUrl);

    const correctAvailableYears = Array.isArray(fetchedLocationObject.availableYears) 
    ? fetchedLocationObject.availableYears.map(String) 
    : [];

    let resolvedLocationName = locationName;
    let wasResolved = false;
    let originalSearch = null;

    if (fetchedLocationObject.resolution) {
      const { resolvedCity, state, originalCity } = fetchedLocationObject.resolution;
      resolvedLocationName = `${resolvedCity}, ${state}`;
      wasResolved = true;
      originalSearch = `${originalCity}, ${state}`;
    }

    if (fetchedLocationObject.requiresCityResolution) {
      return {
        requiresCityResolution: true,
        cityResolutionData: fetchedLocationObject.cityResolutionData,
        locationData: { current: null, availableYears: [] },
        typeData: { features: [], locations: [], metadata: { totalCount: 0 }, aggregateStats: {}, crimeAggregates: {}, availableYears: [] }
      };
    }

    if (specificLocationOnly) {
      const locationProperty = fetchedLocationObject.features?.[0]?.properties || null;
      
      return {
        locationData: {
          current: locationProperty,
          availableYears: correctAvailableYears,
          constituentYears: fetchedLocationObject.constituentYears || null,
          resolvedName: resolvedLocationName,
          wasResolved: wasResolved,
          originalSearch: originalSearch
        },
        typeData: null
      };
    }

    let typeEndpointForCall = endpoint;
    let typeParams = { year: date };
  
    if (locationType === 'county') {
      typeEndpointForCall = 'counties';
      
      if (isNationwideCounties) {
        if (includeGeometry) {
          typeEndpointForCall = 'states';
          typeParams = { year: date, fieldsOnly: 'geometry' };
        } else {
          typeParams = { year: date, geometry: 'false', crime: selectedCrime };
        }
      }
      else if (locationName.startsWith('All ') && locationName.endsWith(' Counties')) {
        let stateName = locationName.replace(/^All /, '').replace(/ Counties$/, '');
        
        if (stateName.length === 2) {
          stateName = getFullStateName(stateName);
        }
        
        if (stateName) {
          typeParams.state = stateName;
          typeParams.crime = selectedCrime;
        }
        
        typeParams.geometry = String(includeGeometry);
      }
      else if (locationName.includes(',')) {
        const { state } = parseCountyLocation(locationName);
        if (state) {
          typeParams.state = state;
          typeParams.crime = selectedCrime;
        }
        typeParams.geometry = String(includeGeometry);
      }
      else {
        typeParams.geometry = String(includeGeometry);
      }
    } 

    if (locationType === 'city') {
      typeEndpointForCall = 'cities';
      
      if (isNationwideCities) {
        if (includeGeometry) {
          typeEndpointForCall = 'states';
          typeParams = { year: date, fieldsOnly: 'geometry' };
        } else {
          typeParams = { year: date, geometry: 'false', crime: selectedCrime };
        }
      }
      else if (locationName.startsWith('All ') && locationName.endsWith(' Cities')) {
        let stateName = locationName.replace(/^All /, '').replace(/ Cities$/, '');
        
        if (stateName.length === 2) {
          stateName = getFullStateName(stateName);
        }
        
        if (stateName) {
          typeParams.state = stateName;
          typeParams.crime = selectedCrime;
        }
        
        typeParams.geometry = String(includeGeometry);
      }
      else if (locationName.includes(',')) {
        const { state } = parseCityLocation(locationName);
        if (state) {
          typeParams.state = state;
          typeParams.crime = selectedCrime;
        }
        typeParams.geometry = String(includeGeometry);
      }
      else {
        typeParams.geometry = String(includeGeometry);
      } 
    }
    
    const typeUrl = buildUrl(`${sourcePath}/${typeEndpointForCall}`, typeParams);
    let fetchedTypeObject;
    
    if ((isNationwideCities || isNationwideCounties) && typeEndpointForCall === 'states' && includeGeometry) {
      const stateCacheKey = `states-geometry-${dataSource}-${date}`;
      
      if (cache[stateCacheKey]) {
        fetchedTypeObject = { features: cache[stateCacheKey] };
      } else {
        fetchedTypeObject = await deduplicatedFetch(typeUrl);
        if (fetchedTypeObject.features) {
          cache[stateCacheKey] = fetchedTypeObject.features;
        }
      }
    } else if (typeUrl === locationUrl) {
      fetchedTypeObject = fetchedLocationObject;
    } else {
      fetchedTypeObject = await deduplicatedFetch(typeUrl);
    }

    let stateFeatures = null;
    if (locationType === 'county' || locationType === 'city' || locationType === 'metro') {
      try {
        const stateParams = { year: date, fieldsOnly: 'geometry' };
        const stateUrl = buildUrl(`${sourcePath}/states`, stateParams);

        const stateCacheKey = `states-geometry-${dataSource}-${date}`;
        
        if (cache[stateCacheKey]) {
          stateFeatures = cache[stateCacheKey];
        } else if (stateUrl === typeUrl) {
          const stateData = fetchedTypeObject;
          stateFeatures = stateData.features || [];
          cache[stateCacheKey] = stateFeatures;
        } else {
          const stateData = await deduplicatedFetch(stateUrl);
          stateFeatures = stateData.features || [];
          cache[stateCacheKey] = stateFeatures;
        }
      } catch (error) {
        console.warn('Could not fetch state boundaries:', error);
      }
    }
    
    if (typeof fetchedTypeObject !== 'object' || fetchedTypeObject === null) {
      fetchedTypeObject = { features: [] };
    }
    if (!Array.isArray(fetchedTypeObject.features)) {
      fetchedTypeObject.features = [];
    }

    const locationsFromType = fetchedTypeObject.features.map(f => f.properties);
    const hasDataInType = fetchedTypeObject.features.some(f => Object.keys(f.properties).length > 1);

    if (!hasDataInType && locationType !== 'nation' && !isNationwideCounties && !locationName.startsWith('All ')) {
      return {
        locationData: {
          current: fetchedLocationObject.features?.[0]?.properties || null,
          availableYears: correctAvailableYears
        },
        typeData: {
          features: [],
          locations: [],
          metadata: { totalCount: 0 },
          aggregateStats: { totals: {}, trends: {}, rates: {}, scores: {} },
          crimeAggregates: {},
          availableYears: correctAvailableYears
        }
      };
    }

    const filteredLocations = locationsFromType.filter(loc => {
      if (locationType === 'city') {
        return loc.city && loc.state;
      }
      
      if (locationType === 'county') {
        if (!loc.county || !loc.state) return false;
        
        return loc.year !== undefined && loc.year !== null;
      }
      
      return true;
    });

    const crimeTypes = ['all_crime', 'violent_crime', 'property_crime', 'murder', 
      'rape', 'robbery', 'aggravated_assault', 'burglary', 'larceny_theft', 'motor_vehicle_theft'];
    
    const crimeAggregates = {};
    
    crimeTypes.forEach(crimeType => {
      const crimePrefix = crimeType.toLowerCase();
      const totalCount = filteredLocations.reduce((sum, loc) => sum + Number(loc[`${crimePrefix}_count`] || 0), 0);
      const totalPrevCount = filteredLocations.reduce((sum, loc) => sum + Number(loc[`${crimePrefix}_count_prev`] || 0), 0);
      const totalPopulation = filteredLocations.reduce((sum, loc) => sum + Number(loc.population || 0), 0);
  
      crimeAggregates[crimeType] = {
        totals: {
          total: totalCount,
          average: Number(filteredLocations[0]?.[`${crimePrefix}_national_count_avg`] || 0),
          stateAverage: Number(filteredLocations[0]?.[`${crimePrefix}_state_count_avg`] || 0),
          locations: filteredLocations.length,
          stateLocations: Number(filteredLocations[0]?.[`total_counties_state`] || 0) + Number(filteredLocations[0]?.[`total_cities_state`] || 0),
          nationalLocations: Number(filteredLocations[0]?.[`total_counties_national`] || 0) + Number(filteredLocations[0]?.[`total_cities_national`] || 0),
        },
        trends: {
          current: totalCount,
          previous: totalPrevCount,
          difference: totalCount - totalPrevCount,
          percentChange: totalPrevCount ? ((totalCount - totalPrevCount) / totalPrevCount) * 100 : 0
        },
        rates: {
          rate: totalPopulation ? Math.round((totalCount / totalPopulation) * 1000000) / 10 : 0,
          nationalRate: Number(filteredLocations[0]?.[`${crimePrefix}_national_rate`] || 0),
          difference: totalPopulation ? 
            Math.round(((totalCount / totalPopulation) * 1000000 - Number(filteredLocations[0]?.[`${crimePrefix}_national_rate`] || 0) * 10)) / 10 : 0,
          percentDiff: totalPopulation && filteredLocations[0]?.[`${crimePrefix}_national_rate`] ? 
            ((((totalCount / totalPopulation) * 100000) - Number(filteredLocations[0][`${crimePrefix}_national_rate`])) / Number(filteredLocations[0][`${crimePrefix}_national_rate`])) * 100 : 0
        },
        scores: {
          compositeScore: filteredLocations.length > 0 ? filteredLocations.reduce((sum, loc) => sum + Number(loc.composite_score || 0), 0) / filteredLocations.length : 0,
          score: filteredLocations.length > 0 ? filteredLocations.reduce((sum, loc) => sum + Number(loc[`${crimePrefix}_score`] || 0), 0) / filteredLocations.length : 0
        }
      };
    });

    let locationProperty;
    if (locationType === 'county') {
      if (locationName && locationName.includes(',')) {
        const { county: countyNameFromFile, state: stateNameFromFile } = parseCountyLocation(locationName);
        
        locationProperty = fetchedLocationObject.features?.find(f => {
          const countyMatch = f.properties.county?.toLowerCase() === countyNameFromFile.toLowerCase();
          const stateMatch = f.properties.state?.toLowerCase() === stateNameFromFile.toLowerCase();
          return countyMatch && stateMatch;
        })?.properties;
      } else if (!isNationwideCounties && !locationName.startsWith('All ')) {
        locationProperty = fetchedLocationObject.features?.[0]?.properties;
      } else if (fetchedLocationObject.aggregateStats) {
        locationProperty = fetchedLocationObject.aggregateStats;
      } else {
        locationProperty = fetchedLocationObject.features?.[0]?.properties;
      }
    } else {
      locationProperty = fetchedLocationObject.features?.find(f => 
        f.properties.name?.toLowerCase() === locationName.toLowerCase()
      )?.properties;
    }

    if (locationType === 'city') {
      if (locationName && locationName.includes(',')) {
        const { city: cityNameFromFile, state: stateNameFromFile } = parseCityLocation(locationName);
        
        if (wasResolved && fetchedLocationObject.features?.[0]?.properties) {
          locationProperty = fetchedLocationObject.features[0].properties;
        } else {
          locationProperty = fetchedLocationObject.features?.find(f => {
            const cityMatch = f.properties.city?.toLowerCase() === cityNameFromFile.toLowerCase();
            const stateMatch = f.properties.state?.toLowerCase() === stateNameFromFile.toLowerCase();
            return cityMatch && stateMatch;
          })?.properties;
        }
      } else if (!locationName.startsWith('All ')) {
        locationProperty = fetchedLocationObject.features?.[0]?.properties;
      } else if (fetchedLocationObject.aggregateStats) {
        locationProperty = fetchedLocationObject.aggregateStats;
      } else {
        locationProperty = fetchedLocationObject.features?.[0]?.properties;
      }
    }
    
    if (!locationProperty && fetchedLocationObject.aggregateStats) {
      locationProperty = fetchedLocationObject.aggregateStats;
    }

    return {
      locationData: {
        current: locationProperty || null,
        availableYears: correctAvailableYears,
        constituentYears: fetchedLocationObject.constituentYears || null,  
        resolvedName: resolvedLocationName,
        wasResolved: wasResolved,
        originalSearch: originalSearch
      },
      typeData: {
        features: fetchedTypeObject.features,
        stateFeatures: stateFeatures,
        locations: filteredLocations,
        metadata: {
          totalCount: filteredLocations.length,
          totalWithGeometry: locationsFromType.length,
          avgCount: crimeAggregates.all_crime?.totals?.average,
          nationalRate: crimeAggregates.all_crime?.rates?.nationalRate
        },
        aggregateStats: crimeAggregates.all_crime,
        crimeAggregates,
        availableYears: correctAvailableYears
      }
    };
  } catch (error) {
    console.error(`Error in fetchLocationData for ${locationName}, ${date}, ${dataSource}:`, error);
    return {
      locationData: { current: null, availableYears: [] },
      typeData: {
        features: [],
        locations: [],
        metadata: { totalCount: 0 },
        aggregateStats: { totals: {}, trends: {}, rates: {}, scores: {} },
        crimeAggregates: {},
        availableYears: []
      },
      error: error.message
    };
  }
};

export const fetchLocationNameHistoricalData = async (locationName, locationType, dataSource = 'FBI_CIUS') => {
  try {
    let endpoint;
    let queryParams = {};
    
    switch (locationType) {
      case 'nation':
        endpoint = 'nation';
        queryParams = { name: locationName, historical: true };
        break;
      case 'region':
        endpoint = 'regions';
        queryParams = { name: locationName, historical: true };
        break;
      case 'division':
        endpoint = 'divisions';
        queryParams = { name: locationName, historical: true };
        break;
      case 'state':
        endpoint = 'states';
        queryParams = { name: locationName, historical: true };
        break;
      case 'metro':
        endpoint = 'metros';
        queryParams = { name: locationName, historical: true };
        break;
      case 'county':
        endpoint = 'counties';
        const { county, state } = parseCountyLocation(locationName);
        if (county && state) {
          queryParams = { county, state, historical: true };
        } else {
          queryParams = { name: locationName, historical: true };
        }
        break;
      case 'city':
        endpoint = 'cities';
        const { city, state: cityState } = parseCityLocation(locationName);
        if (city && cityState) {
          queryParams = { city, state: cityState, historical: true };
        } else {  
          queryParams = { name: locationName, historical: true };
        }
        break;
      default:
        throw new Error('Invalid location type');
    }

    const sourcePath = getSourcePath(dataSource);
    const historicalUrl = buildUrl(`${sourcePath}/${endpoint}`, queryParams);
    const historicalData = await deduplicatedFetch(historicalUrl);

    if (!historicalData.features || historicalData.features.length === 0) {
      return {
        historical: null,
        geometry: null
      };
    }

    return {
      historical: historicalData.features.map(feature => feature.properties),
      geometry: historicalData.features[0].geometry
    };
  } catch (error) {
    console.error('Error fetching historical data:', error);
    throw error;
  }
};

const userRequest = async (path, { method = 'GET', body } = {}) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session) {
    window.location.href = '/login';
    throw new Error('Not authenticated');
  }

  const sessionId = getSessionId();
  const clientUrl = getClientUrl();
  const response = await fetch(`${getApiBaseUrl()}/user/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...(sessionId ? { 'x-session-id': sessionId } : {}),
      ...(clientUrl ? { 'x-client-url': clientUrl } : {})
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const err = new Error(`HTTP error! Status: ${response.status}`);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

const pickArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return [];
};

export const trackSearch = async (name, type, ctx = {}) => {
  return userRequest('track-search', {
    method: 'POST',
    body: { name, type, ...ctx }
  });
};

export const trackExport = async (meta = {}) => {
  return userRequest('track-export', { method: 'POST', body: meta });
};

export const trackPrint = async (meta = {}) => {
  return userRequest('track-print', { method: 'POST', body: meta });
};

export const checkPrintQuota = async () => {
  return userRequest('check-print-quota', { method: 'GET' });
};

export const saveSearch = async (search) => {
  const data = await userRequest('saved-searches', { method: 'POST', body: { search } });
  return pickArray(data, 'saved_searches');
};

export const deleteSavedSearch = async (index) => {
  const data = await userRequest(`saved-searches/${encodeURIComponent(index)}`, { method: 'DELETE' });
  return pickArray(data, 'saved_searches');
};

export const createAlert = async (alertData) => {
  const data = await userRequest('alerts', { method: 'POST', body: alertData });
  return pickArray(data, 'alerts');
};

export const updateAlert = async (id, updates) => {
  const data = await userRequest(`alerts/${encodeURIComponent(id)}`, { method: 'PATCH', body: updates });
  return pickArray(data, 'alerts');
};

export const deleteAlert = async (id) => {
  const data = await userRequest(`alerts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return pickArray(data, 'alerts');
};

export const markNotificationsRead = async (ids) => {
  const data = await userRequest('notifications/read', { method: 'POST', body: { ids } });
  return pickArray(data, 'alert_notifications');
};

export const markAllNotificationsRead = async () => {
  const data = await userRequest('notifications/read-all', { method: 'POST' });
  return pickArray(data, 'alert_notifications');
};